import { useState } from 'react';
import {
  GenerateProductSeoTagsBrandRequestDto,
  GenerateProductSeoTagsBrandResponseDto,
} from '@tiendanube/common';
import { AiFeature } from '@tiendanube/common/src/enums';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { FEATURE_AI_TIER } from 'App/features';
import { AI_PRODUCT_SEO_BRAND_TAGS_ENABLED } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { LanguageInterface, LanguagesType } from 'App/i18n/types';
import { useToast, useTranslationCommon } from 'commons/hooks';
import { logError } from 'commons/logger';
import { useAiUsage } from 'domains/Ai/hooks';
import { useGetLanguage, useGetAllLanguages } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import type {
  AIGenerated,
  AIGeneratedType,
  AIGenerating,
} from 'domains/Catalog/Products/productsSlice';
import { hasSeoForAllLanguages } from 'domains/Catalog/Products/utils';
import { mergeSeoParametersWithAI } from './util';
import { useGenerateProductSeoTagsBrand } from '../../../hooks';

export interface UseTagsAndSeoIAProps {
  name: LanguageInterface;
  description: LanguageInterface;
  tags: string[];
  brand: string;
  seoTitle: LanguageInterface;
  seoDescription: LanguageInterface;
  onChangeTags: ({ name, value }: { name: string; value: string[] }) => void;
  onChangeBrand: ({ name, value }: { name: string; value: string }) => void;
  onChangeSeo: ({
    name,
    value,
    language,
  }: {
    name: string;
    value: LanguageInterface;
    language?: LanguagesType;
  }) => void;
  openModal: () => void;
}

const initialAIGenerated: AIGenerated = {
  seoTitle: {},
  seoDescription: {},
  brand: false,
  tags: false,
};

const initialAIGenerating: AIGenerating = {
  seo: false,
  brand: false,
  tags: false,
};

export const useTagsAndSeoIA = ({
  name,
  description,
  tags,
  brand,
  seoTitle,
  seoDescription,
  onChangeTags,
  onChangeBrand,
  onChangeSeo,
  openModal,
}: UseTagsAndSeoIAProps) => {
  const tCommon = useTranslationCommon();
  const [hasAiProductSeoBrandTagsEnabled] = useHasTags([
    AI_PRODUCT_SEO_BRAND_TAGS_ENABLED,
  ]);
  const { requiredTier, aiUsesUpsellTitle } = useAiUsage();
  const language = useGetLanguage();
  const allLanguages = useGetAllLanguages();
  const { generateSeoTagsBrand } = useGenerateProductSeoTagsBrand();
  const { addToastProgress, closeToastProgress } = useToastProgress();
  const { addToast } = useToast();
  const [isGeneratedAI, setIsGeneratedAI] =
    useState<AIGenerated>(initialAIGenerated);
  const [isGeneratingAI, setIsGeneratingAI] =
    useState<AIGenerating>(initialAIGenerating);

  const handleTypeGeneration = (
    type: AIGeneratedType,
    response: GenerateProductSeoTagsBrandResponseDto,
  ) => {
    const updates: Partial<AIGenerated> = {};

    if (['all', 'tags'].includes(type) && tags.length === 0) {
      onChangeTags({ name: 'tags', value: response.tags });
      updates.tags = true;
    }

    if (['all', 'brand'].includes(type) && brand === '') {
      onChangeBrand({ name: 'brand', value: response.brand });
      updates.brand = true;
    }

    if (['all', 'seo'].includes(type)) {
      const { titles, descriptions, titlesGenerated, descriptionsGenerated } =
        mergeSeoParametersWithAI(
          response.seo,
          seoTitle,
          seoDescription,
          isGeneratedAI,
        );

      onChangeSeo({ name: 'seoTitle', value: titles });
      onChangeSeo({ name: 'seoDescription', value: descriptions });
      updates.seoTitle = titlesGenerated;
      updates.seoDescription = descriptionsGenerated;
    }

    if (Object.keys(updates).length > 0) {
      setIsGeneratedAI((prev) => ({ ...prev, ...updates }));
    }
  };

  const generateAllWithAI = async (type: AIGeneratedType) => {
    if (!name[language] || !description[language]) return;

    addToastProgress({
      label: tCommon('aiGeneration.generating.' + type),
    });

    if (type === 'all') {
      setIsGeneratingAI({
        seo: true,
        brand: true,
        tags: true,
      });
    } else {
      setIsGeneratingAI((prev) => ({ ...prev, [type]: true }));
    }

    openModal();

    try {
      const requestData: GenerateProductSeoTagsBrandRequestDto = {
        product: {
          name: name[language] ?? '',
          description: description[language] ?? '',
        },
        lang: language,
      };
      const response = await generateSeoTagsBrand(requestData);

      handleTypeGeneration(type, response);

      addToast({
        appearance: 'success',
        label: tCommon('aiGeneration.generated.' + type),
      });
    } catch (error) {
      logError('Error generating all content with AI', error);
      addToast({
        appearance: 'danger',
        label: tCommon('aiGeneration.errorGenerating'),
      });
    } finally {
      closeToastProgress();
      setIsGeneratingAI(initialAIGenerating);
    }
  };

  const upsellAll = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCT_SEO_BRAND_TAGS,
    callback: () => generateAllWithAI('all'),
    minValue: requiredTier,
  });

  const handleClickGenerateWithAI = () => {
    if (hasAiProductSeoBrandTagsEnabled) {
      upsellAll();
      return;
    }
    openModal();
  };

  const hasNameAndDescription =
    name[language]?.trim() !== '' && description[language]?.trim() !== '';

  const getTooltipText = () => {
    if (!hasAiProductSeoBrandTagsEnabled) {
      return '';
    }
    if (
      tags.length > 0 &&
      brand.trim() !== '' &&
      hasSeoForAllLanguages(allLanguages, seoTitle, seoDescription)
    ) {
      return tCommon('aiGeneration.alreadyExists.all');
    }
    if (!hasNameAndDescription) {
      return tCommon('aiGeneration.addNameAndDescriptionToGenerate');
    }
    return '';
  };

  const tooltipText = getTooltipText();

  return {
    tooltipText,
    hasNameAndDescription,
    handleClickGenerateWithAI,
    upsellAll,
    setIsGeneratedAI,
    generateAllWithAI,
    isGeneratedAI,
    isGeneratingAI,
  };
};
